import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../app/apiClient";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { currentToken, currentUser, isDomainAdmin, isSessionExpired, logout, otherSessions, Session, switchAccount } from "../pages/login/loginSlice";
import { Account as AccountData, currentAccount, setAccount } from "../pages/account/accountSlice";
import "./AccountMenu.scss";

function getInitials(user: string, displayName?: string | null): string {
    const nameParts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
    if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    }
    if (nameParts.length === 1) {
        return nameParts[0].slice(0, 2).toUpperCase();
    }

    const localPart = user.split("@")[0] || "";
    const parts = localPart.split(/[._+-]+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return "?";
}

function AccountMenu() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(currentUser);
    const token = useAppSelector(currentToken);
    const account = useAppSelector(currentAccount);
    const userIsDomainAdmin = useAppSelector(isDomainAdmin);
    const otherAccounts = useAppSelector(otherSessions);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!token || account) {
            return;
        }
        const abortController = new AbortController();
        // TODO(ONNAECBackend): wire this up to the real getAccount Lambda once it's live.
        apiFetch("/api/account", { method: "GET", token, signal: abortController.signal }).then((response) => {
            if (!response.ok) throw new Error("LOAD_FAILED");
            return response.json();
        }).then((data: AccountData) => {
            dispatch(setAccount(data));
        }).catch(() => {
            // No display name to show yet - the avatar just keeps using email-derived initials.
        });
        return () => abortController.abort();
    }, [token, account, dispatch]);

    const closeMenu = (returnFocus: boolean) => {
        setIsOpen(false);
        if (returnFocus) {
            buttonRef.current?.focus();
        }
    };

    // Switching to a session whose JWT has already expired would just bounce straight into a
    // failed-to-load inbox once it hits the API, so send the user to re-auth that specific
    // account instead of making a dead token active.
    const handleSwitchAccount = (session: Session) => {
        closeMenu(false);
        if (isSessionExpired(session)) {
            navigate(`/login?mode=reauth&email=${encodeURIComponent(session.user ?? "")}`);
        } else {
            dispatch(switchAccount(session.token));
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        const getItems = () => Array.from(
            containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []
        );

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeMenu(true);
                return;
            }
            const items = getItems();
            const currentIndex = items.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown") {
                e.preventDefault();
                items[(currentIndex + 1) % items.length]?.focus();
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                items[(currentIndex - 1 + items.length) % items.length]?.focus();
            } else if (e.key === "Home") {
                e.preventDefault();
                items[0]?.focus();
            } else if (e.key === "End") {
                e.preventDefault();
                items[items.length - 1]?.focus();
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                closeMenu(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            containerRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
    }, [isOpen]);

    if (!user) {
        return null;
    }

    return (
        <div className={"accountMenu"} ref={containerRef}>
            <button
                type="button"
                ref={buttonRef}
                className={"accountMenuTrigger"}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label={`Account menu for ${user}`}
                onClick={() => setIsOpen((open) => !open)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !isOpen) {
                        e.preventDefault();
                        setIsOpen(true);
                    }
                }}
            >
                {getInitials(user, account?.displayName)}
            </button>
            {isOpen && (
                <div className={"accountMenuDropdown"} role="menu" aria-label="Account">
                    <div className={"accountMenuUser"}>{user}</div>
                    <Link to="/account" role="menuitem" className={"accountMenuItem"} onClick={() => closeMenu(false)}>
                        Account settings
                    </Link>
                    {userIsDomainAdmin && (
                        <Link to="/account/domain" role="menuitem" className={"accountMenuItem"} onClick={() => closeMenu(false)}>
                            Domain settings
                        </Link>
                    )}
                    {otherAccounts.length > 0 && (
                        <div className={"accountMenuSectionLabel"}>Switch account</div>
                    )}
                    {otherAccounts.map((session) => {
                        const expired = isSessionExpired(session);
                        return (
                            <button
                                type="button"
                                role="menuitem"
                                key={session.token}
                                className={"accountMenuItem accountMenuSwitchItem"}
                                aria-label={expired ? `Sign in again to ${session.user}` : `Switch to ${session.user}`}
                                onClick={() => handleSwitchAccount(session)}
                            >
                                {session.user}
                                {expired && <span className={"accountMenuExpiredBadge"}>Sign-in required</span>}
                            </button>
                        );
                    })}
                    <Link to="/login?mode=add" role="menuitem" className={"accountMenuItem"} onClick={() => closeMenu(false)}>
                        Add another account
                    </Link>
                    <button
                        type="button"
                        role="menuitem"
                        className={"accountMenuItem logoutItem"}
                        onClick={() => {
                            closeMenu(false);
                            dispatch(logout());
                        }}
                    >
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}

export default AccountMenu;
